class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.dockMedia = window.matchMedia('(min-width: 1200px)');
    this.onOverlayClick = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.onOverlayClick);
    this.setHeaderCartIconAccessibility();
    this.querySelector('#SlCartDockToggle')?.addEventListener('click', () => {
      this.classList.contains('active') ? this.close() : this.open(this.querySelector('#SlCartDockToggle'));
    });
    this.onDockMediaChange = () => this.setupDock();
    this.dockMedia.addEventListener('change', this.onDockMediaChange);
    this.setupDock();
  }

  connectedCallback() {
    if (this.dockMedia) this.setupDock();
    this.panelObserver = new MutationObserver(() => this.syncPanelAccessibility());
    const cartContainer = this.querySelector('#CartDrawer');
    if (cartContainer) this.panelObserver.observe(cartContainer, { childList: true, subtree: true });
  }

  syncPanelAccessibility() {
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;
    panel.inert = this.dockMedia.matches && !this.classList.contains('active');
    panel.setAttribute('role', this.dockMedia.matches ? 'region' : 'dialog');
    if (this.dockMedia.matches) panel.removeAttribute('aria-modal');
    else panel.setAttribute('aria-modal', 'true');
  }

  disconnectedCallback() {
    this.panelObserver?.disconnect();
    this.dockMedia?.removeEventListener('change', this.onDockMediaChange);
    document.body.classList.remove('sl-cart-dock-open');
  }

  setupDock() {
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;
    document.body.classList.remove('sl-cart-dock-open');
    if (this.classList.contains('active')) {
      removeTrapFocus();
      document.body.classList.remove('overflow-hidden');
    }
    this.classList.remove('active');
    if (this.dockMedia.matches) {
      panel.setAttribute('role', 'region');
      panel.removeAttribute('aria-modal');
      let preference = null;
      try { preference = sessionStorage.getItem('shoplaza-cart-dock'); } catch (error) { /* Storage optional. */ }
      if (preference !== 'closed' && !this.classList.contains('is-empty')) this.open();
    } else {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
    }
    this.updateDockToggle();
  }

  updateDockToggle() {
    this.syncPanelAccessibility();
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-haspopup', this.dockMedia.matches ? 'false' : 'dialog');
    const toggle = this.querySelector('#SlCartDockToggle');
    if (!toggle) return;
    const opened = this.classList.contains('active');
    toggle.setAttribute('aria-expanded', String(opened));
    toggle.setAttribute('aria-label', opened ? 'Richiudi il carrello' : 'Apri il carrello');
    const arrow = toggle.querySelector('[data-sl-cart-arrow]');
    if (arrow) arrow.textContent = opened ? '›' : '‹';
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', this.dockMedia.matches ? 'false' : 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.dockMedia.matches) {
      this.classList.add('animate', 'active');
      document.body.classList.add('sl-cart-dock-open');
      const panel = this.querySelector('.drawer__inner');
      panel?.setAttribute('role', 'region');
      panel?.removeAttribute('aria-modal');
      try { sessionStorage.setItem('shoplaza-cart-dock', 'open'); } catch (error) { /* Storage optional. */ }
      this.updateDockToggle();
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
      return;
    }
    if (this.classList.contains('active')) return;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => this.classList.add('animate', 'active'));
    const focusDrawer = () => {
      if (this.dockMedia.matches || !this.classList.contains('active')) return;
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      const target = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (container && target) trapFocus(container, target);
    };
    this.addEventListener('transitionend', focusDrawer, { once: true });
    setTimeout(focusDrawer, 400);
    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('sl-cart-dock-open');
    if (this.dockMedia.matches) {
      try { sessionStorage.setItem('shoplaza-cart-dock', 'closed'); } catch (error) { /* Storage optional. */ }
      this.updateDockToggle();
      this.querySelector('#SlCartDockToggle')?.focus({ preventScroll: true });
      return;
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!sectionElement || typeof html !== 'string') return;
      const contents = this.getSectionInnerHTML(html, section.selector);
      if (contents !== null) sectionElement.innerHTML = contents;
    });
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.onOverlayClick);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);
